import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Button     from "@react/shared/ui/Button"
import IconButton from "@react/shared/ui/IconButton"
import { fmtWhole } from "../helpers/format"
import { TypeChip } from "../helpers/costCategories"
import ProfitabilityHelpModal from "./ProfitabilityHelpModal"
import { updateCategoryBudget } from "../api/valorisation"

// Inline editable money input that returns cents on commit.
const InlineMoneyInput = ({ initialCents, onCommit, disabled }) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft]     = useState("")

  const startEdit = () => {
    if (disabled) return
    setDraft((initialCents / 100).toString().replace(".", ","))
    setEditing(true)
  }
  const commit = () => {
    const normalized = draft.replace(/\s/g, "").replace(",", ".")
    const value      = parseFloat(normalized)
    const cents      = isNaN(value) ? initialCents : Math.round(value * 100)
    setEditing(false)
    onCommit(cents)
  }
  const cancel = () => setEditing(false)

  if (editing) {
    return (
      <input
        autoFocus
        type      = "text"
        inputMode = "decimal"
        value     = {draft}
        onChange  = {e => setDraft(e.target.value)}
        onBlur    = {commit}
        onKeyDown = {e => {
          if (e.key === "Enter")   commit()
          if (e.key === "Escape")  cancel()
        }}
        className = "w-28 h-7 px-2 text-right text-[12px] whitespace-nowrap border border-slate-300 rounded tabular-nums"
      />
    )
  }
  return (
    <button
      type      = "button"
      onClick   = {startEdit}
      disabled  = {disabled}
      className = {`w-28 h-7 text-right text-[12px] whitespace-nowrap tabular-nums px-2 rounded ${
        disabled ? "text-slate-400" : "text-slate-900 hover:bg-blue-50 hover:ring-1 hover:ring-blue-200"
      }`}
    >
      {new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(initialCents / 100)}
    </button>
  )
}

// 0.15 → "15 %", 0.155 → "15,5 %". Rounded before the integer test, or
// 0.15 × 100 lands on 15.000000000000002 and reads "15,0 %".
const coefPct = coef => {
  const pct  = Math.round((Number(coef) || 0) * 10000) / 100
  const text = Number.isInteger(pct) ? String(pct) : String(pct).replace(".", ",")
  return `${text} %`
}

const Row = ({ row, canEdit, onUpdate }) => {
  const ecart    = row.ecart_cents
  const ecartCls = ecart > 0 ? "text-emerald-700" : ecart < 0 ? "text-rose-700" : "text-slate-500"
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50/60">
      {/* La même pastille que dans Marges, Budgets et Dépenses : une catégorie
          analytique se reconnaît à sa couleur et à son icône, pas au libellé
          qu'on relit. */}
      <td className="px-2 py-2"><TypeChip type={row.category} /></td>
      <td className="px-2 py-2 text-right">
        <InlineMoneyInput
          initialCents = {row.sale_budget_cents}
          disabled     = {!canEdit}
          onCommit     = {cents => onUpdate(row.category, { sale_budget_cents: cents })}
        />
      </td>
      <td className="px-2 py-2 text-right">
        <InlineMoneyInput
          initialCents = {row.cost_budget_cents}
          disabled     = {!canEdit}
          onCommit     = {cents => onUpdate(row.category, { cost_budget_cents: cents })}
        />
      </td>
      <td className="px-2 py-2 text-right text-[12px] whitespace-nowrap text-indigo-700 font-semibold tabular-nums">
        {row.coef.toFixed(4).replace(".", ",")}
      </td>
      <td className="px-2 py-2 text-right text-[12px] whitespace-nowrap text-amber-700 tabular-nums">{fmtWhole(row.committed)}</td>
      <td className="px-2 py-2 text-right text-[12px] whitespace-nowrap text-slate-900 tabular-nums">{fmtWhole(row.booked)}</td>
      <td className="px-2 py-2 text-right text-[12px] whitespace-nowrap text-slate-700 tabular-nums">{fmtWhole(row.total_engaged)}</td>
      <td className="px-2 py-2 text-right text-[12px] whitespace-nowrap text-slate-600 tabular-nums">
        {fmtWhole(row.overhead)}
      </td>
      <td className="px-2 py-2 text-right text-[12px] whitespace-nowrap font-semibold text-slate-900 tabular-nums">{fmtWhole(row.cost_price)}</td>
      <td className="px-2 py-2 text-right text-[12px] whitespace-nowrap font-semibold text-blue-700 tabular-nums">{fmtWhole(row.earned_revenue)}</td>
      <td className="px-2 py-2 text-right text-[12px] whitespace-nowrap text-slate-700 tabular-nums">{fmtWhole(row.billed)}</td>
      <td className={`px-2 py-2 text-right text-[12px] whitespace-nowrap font-semibold tabular-nums ${ecartCls}`}>{fmtWhole(row.ecart)}</td>
    </tr>
  )
}

const TotalsRow = ({ totals }) => (
  <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
    <td className="px-2 py-2.5 text-[13px] text-slate-900">{I18n.t("projects.valorisation.total")}</td>
    <td className="px-2 py-2.5 text-right text-[12.5px] whitespace-nowrap tabular-nums">{fmtWhole(totals.sale_budget)}</td>
    <td className="px-2 py-2.5 text-right text-[12.5px] whitespace-nowrap tabular-nums">{fmtWhole(totals.cost_budget)}</td>
    <td className="px-2 py-2.5 text-right text-[12.5px] whitespace-nowrap text-indigo-700 tabular-nums">{totals.coef.toFixed(4).replace(".", ",")}</td>
    <td className="px-2 py-2.5 text-right text-[12.5px] whitespace-nowrap text-amber-700 tabular-nums">{fmtWhole(totals.committed)}</td>
    <td className="px-2 py-2.5 text-right text-[12.5px] whitespace-nowrap tabular-nums">{fmtWhole(totals.booked)}</td>
    <td className="px-2 py-2.5 text-right text-[12.5px] whitespace-nowrap tabular-nums">{fmtWhole(totals.total_engaged)}</td>
    <td className="px-2 py-2.5 text-right text-[12.5px] whitespace-nowrap text-slate-700 tabular-nums">
      {fmtWhole(totals.overhead)}
    </td>
    <td className="px-2 py-2.5 text-right text-[12.5px] whitespace-nowrap text-slate-900 tabular-nums">{fmtWhole(totals.cost_price)}</td>
    <td className="px-2 py-2.5 text-right text-[12.5px] whitespace-nowrap text-blue-700 tabular-nums">{fmtWhole(totals.earned_revenue)}</td>
    <td className="px-2 py-2.5 text-right text-[12.5px] whitespace-nowrap tabular-nums">{fmtWhole(totals.billed)}</td>
    <td className={`px-2 py-2.5 text-right text-[12.5px] whitespace-nowrap tabular-nums ${
      totals.ecart_cents > 0 ? "text-emerald-700" : totals.ecart_cents < 0 ? "text-rose-700" : ""
    }`}>{fmtWhole(totals.ecart)}</td>
  </tr>
)

const ValorisationSection = ({ valorisation: initial, project, policies }) => {
  const [data, setData]         = useState(initial || {})
  const [helpOpen, setHelpOpen] = useState(false)
  const canEdit = !!policies?.can_edit_project

  if (!data?.rows) return null

  const handleUpdate = (category, patch) => {
    updateCategoryBudget(project.id, { cost_category: category, ...patch }).then(response => {
      if (response?.errors) { flashAlert(response.errors.join(", ")); return }
      setData(response)
    })
  }

  return (
    <section id="section-valorisation" className="px-7 scroll-mt-24">
      <header className="pl-[16px] mb-3 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900">
            {I18n.t("projects.valorisation.title")}
          </h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">
            {I18n.t("projects.valorisation.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type      = "button"
            onClick   = {() => setHelpOpen(true)}
            className = "shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-200 text-[12px] font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900"
          >
            <FontAwesomeIcon icon="circle-question" className="text-slate-400" />
            {I18n.t("projects.profitability_help.open")}
          </button>
          {data.csv_url && (
            <a
              href      = {data.csv_url}
              className = "inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-md border border-slate-200"
            >
              <FontAwesomeIcon icon="file-csv" /> {I18n.t("projects.valorisation.export_csv")}
            </a>
          )}
        </div>
      </header>

      <ProfitabilityHelpModal isOpen={helpOpen} onClose={() => setHelpOpen(false)} />

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 text-[10.5px] uppercase tracking-wider text-slate-500 text-left">
                <th className="px-2 py-2.5 font-semibold">{I18n.t("projects.valorisation.col.category")}</th>
                <th className="px-2 py-2.5 font-semibold text-right">{I18n.t("projects.valorisation.col.sale_budget")}</th>
                <th className="px-2 py-2.5 font-semibold text-right">{I18n.t("projects.valorisation.col.cost_budget")}</th>
                <th className="px-2 py-2.5 font-semibold text-right">{I18n.t("projects.valorisation.col.coef")}</th>
                <th className="px-2 py-2.5 font-semibold text-right">{I18n.t("projects.valorisation.col.committed")}</th>
                <th className="px-2 py-2.5 font-semibold text-right">{I18n.t("projects.valorisation.col.booked")}</th>
                <th className="px-2 py-2.5 font-semibold text-right">{I18n.t("projects.valorisation.col.total_engaged")}</th>
                <th className="px-2 py-2.5 font-semibold text-right whitespace-nowrap">
                  {I18n.t("projects.valorisation.col.overhead")}
                  {data.totals?.overhead_coef > 0 && (
                    <span className="ml-1 font-normal normal-case tracking-normal text-slate-400">
                      {coefPct(data.totals.overhead_coef)}
                    </span>
                  )}
                </th>
                <th className="px-2 py-2.5 font-semibold text-right">{I18n.t("projects.valorisation.col.cost_price")}</th>
                <th className="px-2 py-2.5 font-semibold text-right">{I18n.t("projects.valorisation.col.earned_revenue")}</th>
                <th className="px-2 py-2.5 font-semibold text-right">{I18n.t("projects.valorisation.col.billed")}</th>
                <th className="px-2 py-2.5 font-semibold text-right">{I18n.t("projects.valorisation.col.ecart")}</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map(row => (
                <Row key={row.category} row={row} canEdit={canEdit} onUpdate={handleUpdate} />
              ))}
              <TotalsRow totals={data.totals} />
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[11px] text-slate-500 mt-2">
        💡 {I18n.t("projects.valorisation.hint")}
      </p>
    </section>
  )
}

export default ValorisationSection
